/** Deterministic hashing + RNG. `Math.random` is banned in src/world. */

/** 32-bit mix of any number of integers with a seed. Returns uint32. */
export function hash(seed: number, ...xs: number[]): number {
  let h = (seed ^ 0x9e3779b9) >>> 0;
  for (const x of xs) {
    h = Math.imul(h ^ (x | 0), 0x85ebca6b) >>> 0;
    h ^= h >>> 13;
    h = Math.imul(h, 0xc2b2ae35) >>> 0;
    h ^= h >>> 16;
  }
  h = Math.imul(h ^ (h >>> 15), 0x2c1b3c6d) >>> 0;
  h = Math.imul(h ^ (h >>> 12), 0x297a2d39) >>> 0;
  return (h ^ (h >>> 15)) >>> 0;
}

/** Hash to [0,1). */
export function hash01(seed: number, ...xs: number[]): number {
  return hash(seed, ...xs) / 4294967296;
}

/** Hash a string to a uint32 (for keys like "seg:v:3:12"). */
export function hashStr(s: string, seed = 0): number {
  let h = (seed ^ 0x811c9dc5) >>> 0;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 0x01000193) >>> 0;
  return hash(h, s.length);
}

/** Small fast PRNG (sfc32-like) seeded from a uint32. */
export class Rng {
  private a: number; private b: number; private c: number; private d: number;
  constructor(seed: number) {
    this.a = hash(seed, 1); this.b = hash(seed, 2); this.c = hash(seed, 3); this.d = hash(seed, 4) | 1;
    for (let i = 0; i < 8; i++) this.next();
  }
  /** uint32 */
  next(): number {
    const t = (((this.a + this.b) >>> 0) + this.d) >>> 0;
    this.d = (this.d + 1) >>> 0;
    this.a = this.b ^ (this.b >>> 9);
    this.b = (this.c + (this.c << 3)) >>> 0;
    this.c = ((this.c << 21) | (this.c >>> 11)) >>> 0;
    this.c = (this.c + t) >>> 0;
    return t;
  }
  float(): number { return this.next() / 4294967296; }
  int(min: number, maxInclusive: number): number { return min + Math.floor(this.float() * (maxInclusive - min + 1)); }
  chance(p: number): boolean { return this.float() < p; }
  pick<T>(arr: readonly T[]): T { return arr[Math.floor(this.float() * arr.length)]; }
  /** Weighted pick; weights <= 0 are never chosen. Returns undefined if all weights are 0. */
  weighted<T>(arr: readonly T[], weight: (t: T) => number): T | undefined {
    let total = 0;
    for (const t of arr) total += Math.max(0, weight(t));
    if (total <= 0) return undefined;
    let r = this.float() * total;
    for (const t of arr) { r -= Math.max(0, weight(t)); if (r < 0) return t; }
    return arr[arr.length - 1];
  }
}
